import { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { FaDownload, FaMobile, FaDesktop, FaCheckCircle, FaInfoCircle, FaExclamationCircle, FaTimes } from 'react-icons/fa';

/**
 * PWA Install Section Component
 * Permanent section on landing page with install button and QR code
 */
const PWAInstallSection = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('');
  const [swRegistered, setSwRegistered] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [message, _setMessage] = useState({ type: null, text: null }); // type: 'success' | 'info' | 'error' | null
  const isMountedRef = useRef(true);

  const setMessage = (value) => {
    if (isMountedRef.current) {
      _setMessage(value);
    }
  };
  const [isInstalling, setIsInstalling] = useState(false);
  const [_isPWAInstallable, setIsPWAInstallable] = useState(false);
  const [isInstallLink, setIsInstallLink] = useState(false); // true when opened from QR link (?install=true)
  const cleanupRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    let timeoutId = null;

    // Get current URL for QR code - add ?install=true parameter to guide users through install flow
    const baseUrl = window.location.origin;
    const qrUrl = `${baseUrl}?install=true`;
    setCurrentUrl(qrUrl);

    // Track whether this page was opened from an install link (QR code)
    const urlParams = new URLSearchParams(window.location.search);
    const installParam = urlParams.get('install');
    setIsInstallLink(installParam === 'true');

    // Check if app is already installed using multiple methods
    // IMPORTANT: Only trust reliable detection methods, not localStorage alone
    const checkIfInstalled = async () => {
      // Method 1: Check display-mode (MOST RELIABLE - works when app is opened from installed shortcut)
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true);
        setIsChecking(false);
        // Verify and update localStorage flag
        localStorage.setItem('pwa-installed', 'true');
        return true;
      }

      // Method 2: Check iOS standalone mode (RELIABLE)
      if (window.navigator.standalone === true) {
        setIsInstalled(true);
        setIsChecking(false);
        // Verify and update localStorage flag
        localStorage.setItem('pwa-installed', 'true');
        return true;
      }

      // Method 3: Use getInstalledRelatedApps API (RELIABLE - checks actual installation)
      if ('getInstalledRelatedApps' in navigator) {
        try {
          const relatedApps = await navigator.getInstalledRelatedApps();
          if (!isMounted) return false;
          if (relatedApps && relatedApps.length > 0) {
            setIsInstalled(true);
            setIsChecking(false);
            // Store flag for future checks
            localStorage.setItem('pwa-installed', 'true');
            return true;
          } else {
            // If getInstalledRelatedApps returns empty, app is NOT installed
            // Clear any stale localStorage flag
            localStorage.removeItem('pwa-installed');
          }
        } catch (error) {
          // On error, don't trust localStorage flag - clear it to be safe
          localStorage.removeItem('pwa-installed');
        }
      }

      // Method 4: Check localStorage flag ONLY if we can't use reliable methods
      // But verify it's not stale by checking if beforeinstallprompt is available
      // If beforeinstallprompt is available, app is NOT installed (even if flag says it is)
      const installFlag = localStorage.getItem('pwa-installed');
      if (installFlag === 'true') {
        // Verify by checking if beforeinstallprompt is available
      }

      return false;
    };

    // Check if deferredPrompt was already captured in main.jsx
    if (window.__deferredPrompt) {
      setDeferredPrompt(window.__deferredPrompt);
      setIsPWAInstallable(true);
      setIsChecking(false);
    }

    // Set up beforeinstallprompt listener (backup in case main.jsx missed it)
    // This event can fire very early, even before page load completes
    const handleBeforeInstallPrompt = (e) => {
      // Clear any stale localStorage flag - app is definitely not installed
      localStorage.removeItem('pwa-installed');
      setIsInstalled(false); // Ensure installed state is false
      e.preventDefault();
      window.__deferredPrompt = e; // Store globally
      setDeferredPrompt(e);
      setIsPWAInstallable(true);
      setIsChecking(false);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      window.__deferredPrompt = null; // Clear global
      setDeferredPrompt(null);
      // Store installation flag in localStorage for future checks
      localStorage.setItem('pwa-installed', 'true');
      setMessage({ type: 'success', text: 'Yogvital has been installed successfully!' });
      setTimeout(() => setMessage({ type: null, text: null }), 5000);
    };

    // Listen for custom events from main.jsx
    const handlePWAInstallAvailable = (event) => {
      handleBeforeInstallPrompt(event.detail);
    };

    const handlePWAInstalled = () => {
      handleAppInstalled();
    };

    // Set up event listeners (backup in case main.jsx didn't capture it)
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('pwa-install-available', handlePWAInstallAvailable);
    window.addEventListener('pwa-installed', handlePWAInstalled);

    // Store cleanup function reference immediately to prevent memory leaks
    cleanupRef.current = () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('pwa-install-available', handlePWAInstallAvailable);
      window.removeEventListener('pwa-installed', handlePWAInstalled);
    };

    // Check installation status
    checkIfInstalled().then((installed) => {
      if (!isMounted) return;
      if (installed) {
        return; // Already installed, exit early
      }

      // Detect iOS
      const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      setIsIOS(iOS);

      // Detect Android
      const android = /Android/.test(navigator.userAgent);
      setIsAndroid(android);

      // Detect Desktop (Chrome/Edge on desktop support PWA installation)
      const isDesktopBrowser = !iOS && !android && (
        /Chrome/.test(navigator.userAgent) ||
        /Edg/.test(navigator.userAgent) ||
        /Edge/.test(navigator.userAgent)
      );
      setIsDesktop(isDesktopBrowser);

      // Check if service worker is registered (only if not already installed)
      const checkServiceWorker = async () => {
        // Check if HTTPS (required for PWA)
        const isHTTPS = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
        if (!isHTTPS && window.location.hostname !== 'localhost') {
          setIsChecking(false);
          return;
        }

        if ('serviceWorker' in navigator) {
          try {
            // Check if service worker is already registered
            const registration = await navigator.serviceWorker.getRegistration();
            if (!isMounted) return;
            if (registration && registration.active) {
              setSwRegistered(true);
              setIsChecking(false);
              return;
            }

            // In dev mode, service worker might not be registered - that's okay
            // Check if we're in dev mode
            const isDevMode = import.meta.env.DEV;
            if (isDevMode) {
              // In dev mode, we can still show install button (browser might support it)
              setIsChecking(false);
              // Don't set swRegistered to true, but allow install button to show
              return;
            }

            // In production, wait a bit for service worker to register (up to 5 seconds)
            let attempts = 0;
            const maxAttempts = 10;
            const checkInterval = setInterval(async () => {
              attempts++;
              if (!isMounted) {
                clearInterval(checkInterval);
                return;
              }
              const reg = await navigator.serviceWorker.getRegistration();
              if (!isMounted) {
                clearInterval(checkInterval);
                return;
              }
              if (reg && reg.active) {
                setSwRegistered(true);
                clearInterval(checkInterval);
                setIsChecking(false);
              } else if (attempts >= maxAttempts) {
                clearInterval(checkInterval);
                setIsChecking(false);
                // Don't prevent install button from showing - browser might still support installation
              }
            }, 500);
          } catch (error) {
            setIsChecking(false);
            // Don't prevent install button from showing on error
          }
        } else {
          setIsChecking(false);
        }
      };

      checkServiceWorker();

      // Check if PWA is installable by checking manifest and service worker
      const checkPWAInstallability = async () => {
        try {
          // Check if manifest is accessible
          const manifestResponse = await fetch('/manifest.json');
          if (manifestResponse.ok) {
            const manifest = await manifestResponse.json();
            // Check if manifest has required fields
            if (manifest.name && manifest.icons && manifest.icons.length > 0 && swRegistered) {
              // If service worker is registered and manifest is valid, PWA should be installable
            }
          }
        } catch (error) {
          // Silent failure
        }
      };

      // Check installability when service worker is registered
      if (swRegistered) {
        checkPWAInstallability();
      }

      // Add a delayed check for beforeinstallprompt (can take up to 10 seconds, especially on desktop)
      // If beforeinstallprompt doesn't fire, it could mean:
      // 1. App is already installed (verify with getInstalledRelatedApps)
      // 2. Browser doesn't support PWA installation
      // 3. Service worker not registered (dev mode or error)
      // 4. Event hasn't fired yet - try to manually trigger update check
      timeoutId = setTimeout(async () => {
        if (!isMounted) return; // Use local isMounted
        if (deferredPrompt || isInstalled) return; // already handled

        if (!deferredPrompt && !isInstalled) {
          // Check service worker registration directly (not just state)
          let actualSwRegistered = false;
          try {
            const registration = await navigator.serviceWorker.getRegistration();
            actualSwRegistered = !!(registration && registration.active);
          } catch (error) {
            // Silently ignore
          }

          // Check manifest
          try {
            const manifestResponse = await fetch('/manifest.json');
            if (manifestResponse.ok) {
              const manifest = await manifestResponse.json();
              // Check if manifest has required fields
            }
          } catch (error) {
            // Silently ignore
          }

          // Check if app is already installed using all methods
          const checkInstallationStatus = async () => {
            // Check display mode
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

            // Check iOS standalone
            const isIOSStandalone = window.navigator.standalone === true;

            // Check getInstalledRelatedApps
            if ('getInstalledRelatedApps' in navigator) {
              try {
                const relatedApps = await navigator.getInstalledRelatedApps();
                if (relatedApps && relatedApps.length > 0) {
                  // Confirmed: App IS installed
                  setIsInstalled(true);
                  localStorage.setItem('pwa-installed', 'true');
                  return true;
                }
              } catch (error) {
                // Silently ignore
              }
            }

            // If we get here, app is likely NOT installed

            // Clear any stale flag
            localStorage.removeItem('pwa-installed');
            setIsInstalled(false);

            // Update swRegistered state if we found it's actually registered
            if (actualSwRegistered && !swRegistered) {
              setSwRegistered(true);
            }

            // Still allow install button to show (user can try browser menu)
            const isChromium = /Chrome|Chromium|Edg/.test(navigator.userAgent);
            if (isDesktopBrowser || (isChromium && !isInstalled)) {
              setIsPWAInstallable(true);
            }

            return false;
          };

          await checkInstallationStatus();

          // Try to manually trigger update check to see if that helps
          try {
            const registration = await navigator.serviceWorker.getRegistration();
            if (registration) {
              await registration.update();
            }
          } catch (error) {
            // Silently ignore
          }

          setIsChecking(false);
        }
      }, 10000); // Increased to 10 seconds

    });
    // Removed cleanupRef assignment from here

    // Return cleanup function for useEffect

    // Return cleanup function for useEffect
    return () => {
      isMounted = false;
      isMountedRef.current = false;
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInstallClick = async () => {
    if (isInstalled) {
      setMessage({ type: 'info', text: 'Yogvital is already installed on your device!' });
      setTimeout(() => setMessage({ type: null, text: null }), 4000);
      return;
    }

    if (deferredPrompt) {
      try {
        setIsInstalling(true);
        setMessage({ type: null, text: null });
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;

        if (!isMountedRef.current) {
          return;
        }

        if (outcome === 'accepted') {
          setMessage({ type: 'success', text: 'Installing Yogvital...' });
        } else {
          setMessage({ type: 'info', text: 'Install dismissed. You can install anytime from your browser menu.' });
          setTimeout(() => setMessage({ type: null, text: null }), 5000);
        }

        setIsInstalling(false);

        // IMPORTANT: The deferred prompt can be used only once (even if dismissed).
        // Clear it so the UI doesn't get stuck with a "used" prompt event.
        setDeferredPrompt(null);
        window.__deferredPrompt = null;
      } catch (error) {
        setMessage({ type: 'error', text: 'Failed to show install prompt. Please try again.' });
        setTimeout(() => setMessage({ type: null, text: null }), 5000);
        setIsInstalling(false);
        setDeferredPrompt(null); // Clear on error (prompt event may be consumed)
        window.__deferredPrompt = null;
      }
    } else if (isIOS) {
      // iOS installation instructions
      setMessage({
        type: 'info',
        text: 'To install on iOS: Tap the Share button and select "Add to Home Screen"'
      });
      setTimeout(() => setMessage({ type: null, text: null }), 6000);
    } else if (isAndroid) {
      // Android fallback when beforeinstallprompt doesn't fire (common if previously dismissed or heuristic not met)
      setMessage({
        type: 'info',
        text: 'On Android (Chrome): open the browser menu (⋮) and tap "Install app" or "Add to Home screen".'
      });
      setTimeout(() => setMessage({ type: null, text: null }), 8000);
    } else if (isDesktop) {
      // Desktop Chrome/Edge installation instructions
      const isDevMode = import.meta.env.DEV;
      if (swRegistered || isDevMode) {
        // In dev mode or when SW is registered, show install instructions
        setMessage({
          type: 'info',
          text: isDevMode
            ? 'Development mode: Check the browser address bar for the install icon (⊕). Note: Service worker is disabled in dev mode.'
            : 'To install: Click the install icon (⊕) in the address bar'
        });
        setTimeout(() => setMessage({ type: null, text: null }), 8000);
      } else {
        // Service worker not registered and not in dev mode
        setMessage({
          type: 'info',
          text: 'Service worker is registering. Please wait a moment and check the address bar for the install icon (⊕). If it doesn\'t appear, the app may need to be built for production.'
        });
        setTimeout(() => setMessage({ type: null, text: null }), 6000);
      }
    } else {
      // Provide helpful message based on service worker status
      if (!swRegistered) {
        setMessage({
          type: 'info',
          text: 'Service worker is registering. Please wait a moment and try again, or use the QR code to access on mobile.'
        });
        setTimeout(() => setMessage({ type: null, text: null }), 6000);
      } else {
        // Check if it's a browser that might support installation
        const isChromium = /Chrome|Chromium|Edg/.test(navigator.userAgent);
        if (isChromium) {
          setMessage({
            type: 'info',
            text: 'Installation may be available. Look for the install icon in your browser address bar, or use the QR code to access on mobile.'
          });
        } else {
          setMessage({
            type: 'info',
            text: 'Install option may not be available in this browser. Use the QR code to access on mobile, or try Chrome/Edge on Android.'
          });
        }
        setTimeout(() => setMessage({ type: null, text: null }), 6000);
      }
    }
  };

  return (
    <section className="bg-gradient-to-br from-primary/10 via-primary/5 to-white py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8 lg:p-12">
            {/* Left Side - Content */}
            <div className="flex flex-col justify-center">
              <div className="mb-6">
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                  Install Yogvital App
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  Get the best experience with our Progressive Web App. Install it on your device for quick access, offline support, and a native app-like experience.
                </p>

                {/* QR link helper */}
                {isInstallLink && !isInstalled && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-900 font-medium">
                      You opened Yogvital from a QR code. To install, tap <span className="font-semibold">Install App</span> below.
                      If you don’t see a prompt, use your browser menu and choose <span className="font-semibold">Install app</span> / <span className="font-semibold">Add to Home screen</span>.
                    </p>
                  </div>
                )}

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <FaMobile className="text-primary text-xl mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Mobile Access</h3>
                      <p className="text-gray-600 text-sm">Access Yogvital on your phone with a single tap from your home screen.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FaDesktop className="text-primary text-xl mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Offline Support</h3>
                      <p className="text-gray-600 text-sm">Continue using the app even when you're offline.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FaCheckCircle className="text-primary text-xl mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Fast & Secure</h3>
                      <p className="text-gray-600 text-sm">Lightning-fast performance with secure data handling.</p>
                    </div>
                  </div>
                </div>

                {/* Message Display */}
                {message.text && (
                  <div className={`mb-4 p-4 rounded-lg border flex items-start gap-3 ${message.type === 'success'
                    ? 'bg-green-50 border-green-200 text-green-800'
                    : message.type === 'error'
                      ? 'bg-red-50 border-red-200 text-red-800'
                      : 'bg-blue-50 border-blue-200 text-blue-800'
                    }`}>
                    {message.type === 'success' && <FaCheckCircle className="text-green-600 text-xl flex-shrink-0 mt-0.5" />}
                    {message.type === 'error' && <FaExclamationCircle className="text-red-600 text-xl flex-shrink-0 mt-0.5" />}
                    {message.type === 'info' && <FaInfoCircle className="text-blue-600 text-xl flex-shrink-0 mt-0.5" />}
                    <p className="flex-1 text-sm font-medium">{message.text}</p>
                    <button
                      onClick={() => setMessage({ type: null, text: null })}
                      className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                      aria-label="Close message"
                    >
                      <FaTimes className="text-sm" />
                    </button>
                  </div>
                )}

                {/* Install Button */}
                {isInstalled ? (
                  <div className="flex items-center gap-3 px-6 py-4 bg-green-50 border border-green-200 rounded-lg">
                    <FaCheckCircle className="text-green-600 text-2xl" />
                    <div>
                      <p className="font-semibold text-green-900">App Installed</p>
                      <p className="text-sm text-green-700">Yogvital is installed on your device!</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Install button - always show "Install App" for consistency */}
                    <button
                      onClick={handleInstallClick}
                      disabled={isChecking || isInstalling}
                      className="w-full sm:w-auto px-8 py-4 bg-white border-2 border-primary text-primary rounded-lg font-semibold text-lg hover:bg-primary hover:text-white transition-all duration-300 shadow-md hover:shadow-xl flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaDownload className="text-xl" />
                      {isChecking
                        ? 'Checking...'
                        : isInstalling
                          ? 'Installing...'
                          : isIOS
                            ? 'Add to Home Screen'
                            : 'Install App'}
                    </button>
                  </>
                )}

                {/* iOS Instructions */}
                {isIOS && !isInstalled && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-900">
                      <strong>iOS Users:</strong> Tap the Share button <span className="font-mono">□↑</span> in Safari, then select "Add to Home Screen"
                    </p>
                  </div>
                )}

              </div>
            </div>

            {/* Right Side - QR Code */}
            <div className="flex flex-col items-center justify-center">
              <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-gray-200">
                <div className="mb-4 text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Install on Your Phone
                  </h3>
                  <p className="text-sm text-gray-600">
                    Scan to open Yogvital on your phone. Then tap “Install App” when prompted (or from the browser menu).
                  </p>
                </div>
                <div className="flex justify-center p-4 bg-white rounded-lg">
                  <QRCodeSVG
                    value={currentUrl}
                    size={200}
                    level="H"
                    includeMargin={true}
                    fgColor="#00C4B4"
                    bgColor="#ffffff"
                  />
                </div>
                <div className="mt-4 text-center">
                  <p className="text-xs text-gray-500">
                    Or visit: <span className="font-mono text-primary">{window.location.origin}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PWAInstallSection;

